import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, Heart, ShoppingBag, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useStore } from "../../context/StoreProvider";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/shop", label: "Shop" },
  { to: "/collections", label: "Collections" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const {
    cartCount,
    wishlist,
    setCartOpen,
    setSearchOpen,
    menuOpen,
    setMenuOpen,
  } = useStore();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const IconBtn = ({ children, ...props }) => (
    <button
      className="relative flex h-9 w-9 items-center justify-center text-paper transition-colors duration-150 hover:text-brand"
      {...props}
    >
      {children}
    </button>
  );

  return (
    <header
      className={cn(
        "sticky top-0 z-50 bg-ink text-paper transition-shadow duration-200",
        scrolled && "shadow-[0_1px_0_0_rgba(255,255,255,0.12)]"
      )}
      data-testid="site-header"
    >
      <div className="container-9one flex h-16 items-center justify-between gap-4 sm:h-[68px]">
        {/* Left: mobile menu + logo */}
        <div className="flex items-center gap-3">
          <button
            className="flex h-9 w-9 items-center justify-center text-paper hover:text-brand lg:hidden"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
          <Logo />
        </div>

        {/* Center: desktop nav */}
        <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "label-mono link-underline text-paper/80 hover:text-paper",
                  isActive && "text-brand hover:text-brand"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right: actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <IconBtn
            aria-label="Search"
            data-testid="search-toggle-btn"
            onClick={() => setSearchOpen(true)}
          >
            <Search size={21} />
          </IconBtn>
          <Link
            to="/wishlist"
            aria-label="Wishlist"
            data-testid="wishlist-link"
            className="relative flex h-9 w-9 items-center justify-center text-paper transition-colors duration-150 hover:text-brand"
          >
            <Heart size={21} />
            {wishlist.length > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 font-mono text-[10px] font-semibold text-ink"
                data-testid="wishlist-count-badge"
              >
                {wishlist.length}
              </span>
            )}
          </Link>
          <IconBtn
            aria-label="Cart"
            data-testid="cart-toggle-btn"
            onClick={() => setCartOpen(true)}
          >
            <ShoppingBag size={21} />
            {cartCount > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 font-mono text-[10px] font-semibold text-ink"
                data-testid="cart-count-badge"
              >
                {cartCount}
              </span>
            )}
          </IconBtn>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden" data-testid="mobile-menu">
          <div
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm bg-ink text-paper animate-slide-in-right">
            <div className="flex items-center justify-between border-b border-white/10 px-5 h-16">
              <Logo onClick={() => setMenuOpen(false)} />
              <button
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
                onClick={() => setMenuOpen(false)}
                className="text-paper hover:text-brand"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col px-5 py-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "border-b border-white/10 py-4 font-display text-3xl uppercase tracking-tight",
                      isActive ? "text-brand" : "text-paper"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/wishlist"
                onClick={() => setMenuOpen(false)}
                className="border-b border-white/10 py-4 font-display text-3xl uppercase tracking-tight text-paper"
                data-testid="mobile-nav-wishlist"
              >
                Wishlist
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
